import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import StorageIcon from "@material-ui/icons/Storage";
import GroupIcon from "@material-ui/icons/Group";
import DescriptionIcon from "@material-ui/icons/Description";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import SportsEsportsIcon from "@material-ui/icons/SportsEsports";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import { shallowEqual, useSelector } from "react-redux";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { createPlan, deletePlan, getPlanFeatureCatalog, getPlansAdmin, updatePlan } from "../../../../api/plan";

function normalizeId(v) {
	return String(v || "").trim().toLowerCase();
}

function defaultPlan() {
	return {
		id: "",
		name: "",
		price: "0€",
		stripePriceId: "",
		description: "",
		manual: false,
		active: true,
		capabilities: {
			maxCreatedEscapeRooms: 1,
			maxConcurrentPlayableEscapeRooms: 1,
			maxPagesPerEscapeRoom: 0,
			mediaStorageLimit: { value: 50, unit: "MB" },
			maxConcurrentPlayers: 0,
			teamPlayEnabled: false,
			maxCollaborators: 0,
			featureFlags: [],
		},
	};
}

function toInt(value, fallback = 0) {
	const n = Number(value);
	if (!Number.isFinite(n)) return fallback;
	return Math.max(0, Math.floor(n));
}

function formatLimit(value, { unlimitedLabel = "Ilimitado", unit = "" } = {}) {
	const n = Number(value || 0);
	if (!Number.isFinite(n) || n === 0) return unlimitedLabel;
	return `${n}${unit ? ` ${unit}` : ""}`;
}

export default function PlansAdminPage() {
	const authUser = useSelector((s) => s.authentication?.user, shallowEqual);
	const isAdmin = String(authUser?.role || "").toLowerCase() === "admin";

	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [featureCatalog, setFeatureCatalog] = useState([]);

	const [open, setOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [mode, setMode] = useState("create"); // create | edit
	const [form, setForm] = useState(defaultPlan());

	const load = useCallback(async () => {
		if (!isAdmin) return;
		setLoading(true);
		try {
			const res = await getPlansAdmin();
			if (res.status === 200 && Array.isArray(res.data)) setRows(res.data);
			else setRows([]);
		} catch (error) {
			setRows([]);
			alertError({ error, customMessage: "No se pudieron cargar los planes." });
		} finally {
			setLoading(false);
		}
	}, [isAdmin]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await getPlanFeatureCatalog();
				if (cancelled) return;
				if (res.status === 200 && Array.isArray(res.data)) setFeatureCatalog(res.data);
			} catch {
				// no-op
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const sortedRows = useMemo(() => {
		return [...rows].sort((a, b) => (Number(a.rank || 0) - Number(b.rank || 0)) || String(a.id).localeCompare(String(b.id)));
	}, [rows]);

	const openCreate = () => {
		setMode("create");
		setForm(defaultPlan());
		setOpen(true);
	};

	const openEdit = (plan) => {
		const featureFlags = Array.isArray(plan?.capabilities?.featureFlags) ? plan.capabilities.featureFlags : [];
		const collabEnabled = Boolean(plan?.capabilities?.teamPlayEnabled || featureFlags.includes("collaborators"));
		setMode("edit");
		setForm({
			id: plan.id || "",
			name: plan.name || "",
			price: plan.price || "",
			stripePriceId: plan.stripePriceId || "",
			description: plan.description || "",
			manual: Boolean(plan.manual),
			active: plan.active !== false,
			capabilities: {
				maxCreatedEscapeRooms: toInt(
					plan?.capabilities?.maxCreatedEscapeRooms ?? plan?.capabilities?.maxOwnedEscapeRooms,
					1
				),
				maxConcurrentPlayableEscapeRooms: toInt(plan?.capabilities?.maxConcurrentPlayableEscapeRooms, 1),
				maxPagesPerEscapeRoom: toInt(plan?.capabilities?.maxPagesPerEscapeRoom, 0),
				mediaStorageLimit: {
					value: toInt(plan?.capabilities?.mediaStorageLimit?.value, 0),
					unit: String(plan?.capabilities?.mediaStorageLimit?.unit || "MB").toUpperCase() === "GB" ? "GB" : "MB",
				},
				maxConcurrentPlayers: toInt(plan?.capabilities?.maxConcurrentPlayers, 0),
				teamPlayEnabled: collabEnabled,
				maxCollaborators: toInt(plan?.capabilities?.maxCollaborators, 0),
				featureFlags: featureFlags,
			},
		});
		setOpen(true);
	};

	const onSave = async () => {
		const id = normalizeId(form.id);
		if (!id) return alertError({ error: null, customMessage: "El campo id es obligatorio." });
		setSaving(true);
		try {
			const caps = form.capabilities || {};
			const featureFlags = Array.isArray(caps.featureFlags) ? caps.featureFlags : [];
			const collabEnabled = Boolean(caps.teamPlayEnabled || featureFlags.includes("collaborators"));
			const createdCap = toInt(caps.maxCreatedEscapeRooms ?? caps.maxOwnedEscapeRooms, 1);
			const payloadCaps = {
				maxCreatedEscapeRooms: createdCap,
				/** Misma cifra que creadas: el backend la sincroniza; la enviamos explícita por compatibilidad. */
				maxOwnedEscapeRooms: createdCap,
				maxConcurrentPlayableEscapeRooms: toInt(caps.maxConcurrentPlayableEscapeRooms, 1),
				maxPagesPerEscapeRoom: toInt(caps.maxPagesPerEscapeRoom, 0),
				mediaStorageLimit: {
					value: toInt(caps.mediaStorageLimit?.value, 0),
					unit: String(caps.mediaStorageLimit?.unit || "MB").toUpperCase() === "GB" ? "GB" : "MB",
				},
				maxConcurrentPlayers: toInt(caps.maxConcurrentPlayers, 0),
				teamPlayEnabled: collabEnabled,
				maxCollaborators: collabEnabled ? toInt(caps.maxCollaborators, 0) : 0,
				featureFlags: featureFlags,
			};
			const payload = {
				id,
				name: String(form.name || "").trim() || id,
				price: String(form.price || "").trim(),
				stripePriceId: String(form.stripePriceId || "").trim(),
				description: String(form.description || "").trim(),
				manual: Boolean(form.manual),
				active: Boolean(form.active),
				capabilities: payloadCaps,
			};
			if (mode === "create") {
				await createPlan(payload);
				alertSuccess({ customMessage: "Plan creado." });
			} else {
				await updatePlan(id, payload);
				alertSuccess({ customMessage: "Plan actualizado." });
			}
			setOpen(false);
			await load();
		} catch (error) {
			alertError({ error, customMessage: "No se pudo guardar el plan." });
		} finally {
			setSaving(false);
		}
	};

	const onDelete = async (id) => {
		const pid = normalizeId(id);
		if (!pid) return;
		// eslint-disable-next-line no-alert
		const ok = window.confirm(`¿Eliminar el plan "${pid}"?`);
		if (!ok) return;
		try {
			await deletePlan(pid);
			alertSuccess({ customMessage: "Plan eliminado." });
			await load();
		} catch (error) {
			alertError({ error, customMessage: "No se pudo eliminar el plan." });
		}
	};

	if (!isAdmin) {
		return (
			<Card>
				<CardHeader title="Planes" />
				<CardBody>
					<Typography color="textSecondary">Solo los administradores pueden gestionar los planes.</Typography>
				</CardBody>
			</Card>
		);
	}

	return (
		<>
			<Card>
				<CardHeader title="Planes (colección: planes)" />
				<CardBody>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
						<Typography variant="body2" color="textSecondary">
							Edita nombre, precio y <strong>límites</strong> (escape rooms creados, activos en juego, páginas, etc.). El <strong>rank</strong> y la lógica de cobro se calculan automáticamente a partir del precio.
						</Typography>
						<Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
							Nuevo plan
						</Button>
					</div>

					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress />
						</div>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>id</TableCell>
										<TableCell>Nombre</TableCell>
										<TableCell>Precio</TableCell>
										<TableCell>Escape rooms creados</TableCell>
										<TableCell>Escape rooms activos</TableCell>
										<TableCell>Páginas</TableCell>
										<TableCell>Almacenamiento</TableCell>
										<TableCell>Jugadores</TableCell>
										<TableCell>Equipo</TableCell>
										<TableCell>Activo</TableCell>
										<TableCell align="right">Acciones</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{sortedRows.length === 0 ? (
										<TableRow>
											<TableCell colSpan={11} align="center">
												No hay planes.
											</TableCell>
										</TableRow>
									) : (
										sortedRows.map((p) => (
											<TableRow key={p.id}>
												<TableCell>{p.id}</TableCell>
												<TableCell>{p.name}</TableCell>
												<TableCell>
													{p.price}
													{p.priceSuffix || ""}
												</TableCell>
												<TableCell>
													{formatLimit(p?.capabilities?.maxCreatedEscapeRooms ?? p?.capabilities?.maxOwnedEscapeRooms, {
														unlimitedLabel: "Ilimitado",
													})}
												</TableCell>
												<TableCell>{formatLimit(p?.capabilities?.maxConcurrentPlayableEscapeRooms, { unlimitedLabel: "Ilimitado" })}</TableCell>
												<TableCell>{formatLimit(p?.capabilities?.maxPagesPerEscapeRoom, { unlimitedLabel: "Ilimitado" })}</TableCell>
												<TableCell>
													{formatLimit(p?.capabilities?.mediaStorageLimit?.value, { unlimitedLabel: "Ilimitado", unit: p?.capabilities?.mediaStorageLimit?.unit || "MB" })}
												</TableCell>
												<TableCell>{formatLimit(p?.capabilities?.maxConcurrentPlayers, { unlimitedLabel: "Ilimitado" })}</TableCell>
												<TableCell>
													{p?.capabilities?.teamPlayEnabled ? `Sí (${formatLimit(p?.capabilities?.maxCollaborators, { unlimitedLabel: "Ilimitado" })})` : "No"}
												</TableCell>
												<TableCell>{p.active === false ? "No" : "Sí"}</TableCell>
												<TableCell align="right">
													<Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(p)}>
														Editar
													</Button>
													<Button size="small" startIcon={<DeleteIcon />} onClick={() => onDelete(p.id)}>
														Eliminar
													</Button>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardBody>
			</Card>

			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>{mode === "create" ? "Crear plan" : `Editar plan: ${normalizeId(form.id)}`}</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} style={{ marginTop: 6 }}>
						<Grid item xs={12} sm={6}>
							<TextField
								label="id (slug)"
								variant="outlined"
								size="small"
								value={form.id}
								onChange={(e) => {
									const v = e?.target?.value;
									setForm((s) => ({ ...s, id: v }));
								}}
								disabled={mode === "edit"}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Nombre"
								variant="outlined"
								size="small"
								value={form.name}
								onChange={(e) => {
									const v = e?.target?.value;
									setForm((s) => ({ ...s, name: v }));
								}}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Precio"
								variant="outlined"
								size="small"
								value={form.price}
								onChange={(e) => {
									const v = e?.target?.value;
									setForm((s) => ({ ...s, price: v }));
								}}
								helperText="Ej: 0€ (gratis), 19,99€, 49,99€."
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Stripe Price ID"
								variant="outlined"
								size="small"
								value={form.stripePriceId}
								onChange={(e) => {
									const v = e?.target?.value;
									setForm((s) => ({ ...s, stripePriceId: v }));
								}}
								helperText="price_xxx del Dashboard de Stripe (planes de pago). Vacío si es gratis o manual."
								fullWidth
								disabled={Boolean(form.manual)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControlLabel
								control={
									<Switch
										checked={Boolean(form.manual)}
										onChange={(e) => {
											const checked = Boolean(e?.target?.checked);
											setForm((s) => ({ ...s, manual: checked }));
										}}
									/>
								}
								label="Manual"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={Boolean(form.active)}
										onChange={(e) => {
											const checked = Boolean(e?.target?.checked);
											setForm((s) => ({ ...s, active: checked }));
										}}
									/>
								}
								label="Activo"
							/>
						</Grid>
						<Grid item xs={12}>
							<Typography variant="subtitle2" style={{ fontWeight: 800, color: "#111827" }}>
								Límites de escape rooms
							</Typography>
							<Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 4, marginBottom: 0 }}>
								<strong>Creados</strong>: cuántas salas puede tener el usuario en total. <strong>Activos</strong>: cuántas puede tener en juego a la vez (simultáneas). En ambos casos, <strong>0 = ilimitado</strong>.
							</Typography>
						</Grid>

						<Grid item xs={12} md={6}>
							<Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
								<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
									<MeetingRoomIcon style={{ color: "#6d28d9" }} />
									<div style={{ fontWeight: 800 }}>Escape rooms creados</div>
								</div>
								<TextField
									label="Límite de escape rooms creados"
									variant="outlined"
									size="small"
									type="number"
									inputProps={{ min: 0 }}
									value={form.capabilities.maxCreatedEscapeRooms}
									onChange={(e) => {
										const v = e?.target?.value;
										setForm((s) => ({
											...s,
											capabilities: { ...s.capabilities, maxCreatedEscapeRooms: toInt(v, 0) },
										}));
									}}
									helperText="0 = ilimitado"
									fullWidth
								/>
							</Paper>
						</Grid>

						<Grid item xs={12} md={6}>
							<Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
								<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
									<SportsEsportsIcon style={{ color: "#6d28d9" }} />
									<div style={{ fontWeight: 800 }}>Escape rooms activos</div>
								</div>
								<TextField
									label="Límite de escape rooms activos (en juego)"
									variant="outlined"
									size="small"
									type="number"
									inputProps={{ min: 0 }}
									value={form.capabilities.maxConcurrentPlayableEscapeRooms}
									onChange={(e) => {
										const v = e?.target?.value;
										setForm((s) => ({
											...s,
											capabilities: { ...s.capabilities, maxConcurrentPlayableEscapeRooms: toInt(v, 0) },
										}));
									}}
									helperText="0 = ilimitado (cuántas simultáneas)"
									fullWidth
								/>
							</Paper>
						</Grid>

						<Grid item xs={12}>
							<TextField
								label="Descripción"
								variant="outlined"
								size="small"
								value={form.description}
								onChange={(e) => {
									const v = e?.target?.value;
									setForm((s) => ({ ...s, description: v }));
								}}
								fullWidth
							/>
						</Grid>

						<Grid item xs={12}>
							<div style={{ fontWeight: 800, color: "#111827", marginBottom: 8 }}>Otros límites</div>
						</Grid>

						<Grid item xs={12} md={6}>
							<Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
								<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
									<DescriptionIcon style={{ color: "#6d28d9" }} />
									<div style={{ fontWeight: 800 }}>Páginas por Escape Room</div>
								</div>
								<TextField
									label="Límite (0 = ilimitado)"
									variant="outlined"
									size="small"
									type="number"
									value={form.capabilities.maxPagesPerEscapeRoom}
									onChange={(e) => {
										const v = e?.target?.value;
										setForm((s) => ({
											...s,
											capabilities: { ...s.capabilities, maxPagesPerEscapeRoom: toInt(v, 0) },
										}));
									}}
									fullWidth
								/>
							</Paper>
						</Grid>

						<Grid item xs={12} md={6}>
							<Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
								<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
									<PhotoLibraryIcon style={{ color: "#6d28d9" }} />
									<div style={{ fontWeight: 800 }}>Almacenamiento multimedia</div>
								</div>
								<div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10 }}>
									<TextField
										label="Límite (0 = ilimitado)"
										variant="outlined"
										size="small"
										type="number"
										value={form.capabilities.mediaStorageLimit.value}
										onChange={(e) => {
											const v = e?.target?.value;
											setForm((s) => ({
												...s,
												capabilities: {
													...s.capabilities,
													mediaStorageLimit: { ...s.capabilities.mediaStorageLimit, value: toInt(v, 0) },
												},
											}));
										}}
									/>
									<Select
										variant="outlined"
										value={form.capabilities.mediaStorageLimit.unit}
										onChange={(e) => {
											const unit = e?.target?.value;
											setForm((s) => ({
												...s,
												capabilities: {
													...s.capabilities,
													mediaStorageLimit: { ...s.capabilities.mediaStorageLimit, unit },
												},
											}));
										}}
									>
										<MenuItem value="MB">MB</MenuItem>
										<MenuItem value="GB">GB</MenuItem>
									</Select>
								</div>
							</Paper>
						</Grid>

						<Grid item xs={12} md={6}>
							<Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
								<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
									<StorageIcon style={{ color: "#6d28d9" }} />
									<div style={{ fontWeight: 800 }}>Jugadores simultáneos</div>
								</div>
								<TextField
									label="Límite (0 = ilimitado)"
									variant="outlined"
									size="small"
									type="number"
									value={form.capabilities.maxConcurrentPlayers}
									onChange={(e) => {
										const v = e?.target?.value;
										setForm((s) => ({
											...s,
											capabilities: { ...s.capabilities, maxConcurrentPlayers: toInt(v, 0) },
										}));
									}}
									fullWidth
								/>
							</Paper>
						</Grid>

						<Grid item xs={12}>
							<Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<GroupIcon style={{ color: "#6d28d9" }} />
										<div style={{ fontWeight: 800 }}>Compartir escape rooms para coeditar</div>
									</div>
									<FormControlLabel
										control={
											<Switch
												checked={Boolean(form.capabilities.teamPlayEnabled)}
												onChange={(e) => {
													const checked = Boolean(e?.target?.checked);
													setForm((s) => ({
														...s,
														capabilities: {
															...s.capabilities,
															teamPlayEnabled: checked,
															maxCollaborators: checked ? s.capabilities.maxCollaborators : 0,
															featureFlags: checked
																? (s.capabilities.featureFlags.includes("collaborators") ? s.capabilities.featureFlags : [...s.capabilities.featureFlags, "collaborators"])
																: s.capabilities.featureFlags.filter((k) => k !== "collaborators"),
														},
													}));
												}}
											/>
										}
										label={form.capabilities.teamPlayEnabled ? "Sí" : "No"}
									/>
								</div>
								<div style={{ marginTop: 10 }}>
									<TextField
										label="Límite de coeditores por escape room (0 = ilimitado)"
										variant="outlined"
										size="small"
										type="number"
										disabled={!form.capabilities.teamPlayEnabled}
										value={form.capabilities.maxCollaborators}
										onChange={(e) => {
											const v = e?.target?.value;
											setForm((s) => ({
												...s,
												capabilities: { ...s.capabilities, maxCollaborators: toInt(v, 0) },
											}));
										}}
										fullWidth
									/>
								</div>
							</Paper>
						</Grid>

						<Grid item xs={12}>
							<div style={{ fontWeight: 800, color: "#111827", marginBottom: 8 }}>Funcionalidades</div>
							{featureCatalog.length === 0 ? (
								<Typography variant="body2" color="textSecondary">
									No se pudo cargar el catálogo de funcionalidades.
								</Typography>
							) : (
								<Grid container spacing={2}>
									{featureCatalog.map((f) => {
										const checked = form.capabilities.featureFlags.includes(f.key);
										return (
											<Grid item xs={12} md={6} key={f.key}>
												<Paper
													variant="outlined"
													style={{
														padding: 12,
														borderRadius: 12,
														borderColor: checked ? "#7c3aed" : undefined,
														boxShadow: checked ? "0 10px 18px rgba(124,58,237,0.10)" : undefined,
													}}
												>
													<div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
														<Checkbox
															checked={checked}
															onChange={(e) => {
																const checked = Boolean(e?.target?.checked);
																const next = checked
																	? [...form.capabilities.featureFlags, f.key]
																	: form.capabilities.featureFlags.filter((k) => k !== f.key);
																setForm((s) => ({
																	...s,
																	capabilities: { ...s.capabilities, featureFlags: next },
																}));
															}}
															color="primary"
														/>
														<div style={{ flex: 1 }}>
															<div style={{ fontWeight: 800, color: "#111827" }}>{f.label}</div>
															<div style={{ marginTop: 2, fontSize: 13, color: "#6b7280" }}>{f.description}</div>
														</div>
													</div>
												</Paper>
											</Grid>
										);
									})}
								</Grid>
							)}
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)} disabled={saving}>
						Cancelar
					</Button>
					<Button onClick={onSave} color="primary" variant="contained" disabled={saving}>
						{saving ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

